/** 展立 DPI 卡片 — XY 独立由页面顶部全局开关控制，卡片内不显示开关 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Iconfont, Slider } from "@eweadn-qiankun/shared-components";
import type { DpiProfile } from "../types";
import "../DpiCard.scss";

export interface ZhanliDpiCardProps {
  profile: DpiProfile;
  index: number;
  isActive: boolean;
  canDelete?: boolean;
  maxDpiValue: number;
  minDpiValue: number;
  dpiStep: number;
  isOperating?: boolean;
  onSelect: (index: number) => void;
  onXValueChange: (index: number, value: number) => void;
  onXValueChangeComplete: (index: number, value: number) => void;
  onYValueChange: (index: number, value: number) => void;
  onYValueChangeComplete: (index: number, value: number) => void;
  onDelete: (index: number) => void;
  onColorClick: (index: number) => void;
  colorButtonRef: (el: HTMLElement | null) => void;
  selectable?: boolean;
}

export function ZhanliDpiCard({
  profile,
  index,
  isActive,
  canDelete = true,
  maxDpiValue,
  minDpiValue,
  dpiStep,
  isOperating = false,
  onSelect,
  onXValueChange,
  onXValueChangeComplete,
  onYValueChange,
  onYValueChangeComplete,
  onDelete,
  onColorClick,
  colorButtonRef,
  selectable = true,
}: ZhanliDpiCardProps): React.ReactElement {
  const { t } = useTranslation();

  const colorTitle = `${t("mouse.dpi.currentColor")}: rgb(${profile.color.r},${profile.color.g},${profile.color.b})`;

  return (
    <div
      className={`dpi-card-wrapper${selectable ? "" : " dpi-card-wrapper--readonly"}`}
      onClick={
        selectable
          ? (e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("button, input, .el-slider-box, .switch") ||
                target.closest(".dpi-slider-wrap")
              ) {
                return;
              }
              onSelect(index);
            }
          : undefined
      }
    >
      <div className={`dpi-card ${isActive ? "active" : ""}`}>
        <div className="card-header">
          <div
            ref={colorButtonRef}
            className="card-title-block"
            style={{
              cursor: profile.enabled ? "pointer" : "not-allowed",
            }}
            title={colorTitle}
            onClick={(e) => {
              e.stopPropagation();
              if (profile.enabled) onColorClick(index);
            }}
          >
            <div
              className="color-button"
              style={{
                backgroundColor: `rgb(${profile.color.r},${profile.color.g},${profile.color.b})`,
              }}
            />
            <h3 className="card-title">DPI{index + 1}</h3>
          </div>

          <div className="card-header-right">
            {canDelete && (
              <button
                className="icon-btn"
                onClick={() => onDelete(index)}
                disabled={isOperating}
                title={t("mouse.dpi.deleteDpi")}
              >
                <Iconfont icon="delete" className="icon-btn" />
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          {!profile.xyIndependent ? (
            <div className="slider-row combined">
              <label>{t("mouse.dpi.xyAxis")}</label>
              <div className="dpi-slider-wrap">
                <Slider
                  value={profile.xValue}
                  min={minDpiValue}
                  max={maxDpiValue}
                  step={dpiStep}
                  onChange={(v) => onXValueChange(index, v)}
                  onChangeComplete={(v) => onXValueChangeComplete(index, v)}
                  disabled={!profile.enabled}
                  showValue
                  valueFormatter={(v) => String(v)}
                  unit=""
                  className="dpi-slider"
                />
              </div>
            </div>
          ) : (
            <div className="slider-group separate">
              <div className="slider-row">
                <label>{t("mouse.dpi.xAxis")}</label>
                <div className="dpi-slider-wrap">
                  <Slider
                    value={profile.xValue}
                    min={minDpiValue}
                    max={maxDpiValue}
                    step={dpiStep}
                    onChange={(v) => onXValueChange(index, v)}
                    onChangeComplete={(v) => onXValueChangeComplete(index, v)}
                    disabled={!profile.enabled}
                    showValue
                    valueFormatter={(v) => String(v)}
                    unit=""
                    className="dpi-slider"
                  />
                </div>
              </div>
              <div className="slider-row">
                <label>{t("mouse.dpi.yAxis")}</label>
                <div className="dpi-slider-wrap">
                  <Slider
                    value={profile.yValue}
                    min={minDpiValue}
                    max={maxDpiValue}
                    step={dpiStep}
                    onChange={(v) => onYValueChange(index, v)}
                    onChangeComplete={(v) => onYValueChangeComplete(index, v)}
                    disabled={!profile.enabled}
                    showValue
                    valueFormatter={(v) => String(v)}
                    unit=""
                    className="dpi-slider"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
